import React, { useContext, useEffect, useState } from 'react'
import { DataContext } from '../../App'
import { Demand } from '../../api/schemas/schema'
import { Button, makeStyles, MessageBar, MessageBarBody, MessageBarTitle, shorthands, tokens } from '@fluentui/react-components'
import { t } from '../../i18n/i18n'
import { demandColumns } from '../../helpers/columnHelper'
import { demandExpansion } from '../../helpers/dndHelper'
import { Loader } from '../../components/common/spinner/Loader'
import { setLoading } from '../../helpers/stateHelper'
import { Modal } from '../../components/common/modal/Modal'
import { onDemandMove } from '../../helpers/demandHelper'
import { Demands } from './Demands'
import { DemandSearchBox } from '../../components/demand/DemandSearchBox'
import { getDemandSearchBoxLabel, getTargetDemandLevel } from '../../helpers/optionsHelper'
import { api } from '../../helpers/apiHelper'

const useStyles = makeStyles({
   row: {
      display: 'flex',
      gap: tokens.spacingVerticalS,
      boxSizing: 'border-box',
      ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
      ...shorthands.padding(tokens.spacingVerticalS, 0),
      '&:hover': {
         backgroundColor: tokens.colorNeutralBackground1Hover,
      },
      '&:active': {
         backgroundColor: tokens.colorNeutralBackground1Pressed,
      },
   },
   colheader: {
      fontWeight: tokens.fontWeightBold,
   },
   modalHeight: {
      minHeight: '80px',
      overflow: 'visible',
   },
})

type CustomeDemandTableProps = {
   dialogOpen: boolean
   expandAllDemands: boolean
   moveDemandModalClosed: () => void
}

export const CustomDemandTable = ({ dialogOpen, expandAllDemands, moveDemandModalClosed }: CustomeDemandTableProps) => {
   const classes = useStyles()
   const dataContext = useContext(DataContext)
   const [showButton, setShowButton] = useState<string>('')
   const [loadingRows, setLoadingRows] = useState<string[]>([])
   const { demands, selectedTab, isLoading, accessToken } = dataContext.state

   const [archivedDemands, setArchivedDemands] = useState<Demand[]>(null)
   const [targetDemand, setTargetDemand] = useState<Demand>()

   const toggleRowExpansion = (demandId: string): void => {
      demandExpansion(dataContext, demandId, setLoadingRows)
   }

   const onModalClose = () => {
      moveDemandModalClosed()
      setTargetDemand(undefined)
   }

   useEffect(() => {
      if (demands) {
         ;(async () => {
            setLoading(dataContext.setRootState, true)
            switch (selectedTab) {
               case 'archivedDemands':
                  const archivedDemandsResult = (await api(accessToken).demandArchiveList()).data
                  setArchivedDemands(archivedDemandsResult)
                  // eslint-disable-next-line array-callback-return
                  archivedDemandsResult?.map((archivedDemand) => {
                     toggleRowExpansion(archivedDemand.id)
                  })

                  setLoading(dataContext.setRootState, false)
                  break
               case 'activeDemands':
               default:
                  setArchivedDemands(null)
                  setLoading(dataContext.setRootState, false)
                  break
            }
         })()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectedTab])

   return (
      <div role="table">
         <div role="rowgroup">
            <div role="row" className={classes.row}>
               {demandColumns().map((col) => (
                  <span key={col.columnId} role="columnheader" className={classes.colheader} style={{ width: col.width, minWidth: col.minWidth }}>
                     {col.label}
                  </span>
               ))}
            </div>
         </div>
         {isLoading ? (
            <Loader />
         ) : selectedTab === 'archivedDemands' && archivedDemands && archivedDemands.length === 0 ? (
            <MessageBar>
               <MessageBarBody>
                  <MessageBarTitle>{t('NoArchivedDemands')}</MessageBarTitle>
               </MessageBarBody>
            </MessageBar>
         ) : (
            <>
               <Demands
                  demands={archivedDemands ?? demands}
                  loadingRows={loadingRows}
                  showButton={showButton}
                  expandAllDemands={expandAllDemands}
                  setShowButton={setShowButton}
                  toggleRowExpansion={toggleRowExpansion}
               />
               <Modal
                  open={dialogOpen}
                  title={t('MoveDemand')}
                  actions={
                     <>
                        <Button onClick={onModalClose} disabled={isLoading}>
                           {t('Cancel')}
                        </Button>
                        <Button
                           disabled={!targetDemand || isLoading}
                           appearance="primary"
                           onClick={() => {
                              onDemandMove(dataContext, targetDemand, onModalClose)
                           }}
                        >
                           {t('Move')}
                        </Button>
                     </>
                  }
                  contentClassname={classes.modalHeight}
                  children={
                     <DemandSearchBox
                        targetDemandLevel={getTargetDemandLevel(dataContext)}
                        targetDemand={targetDemand}
                        placeholder={t('DemandModalSearchParams')}
                        setTargetDemand={setTargetDemand}
                        label={getDemandSearchBoxLabel(getTargetDemandLevel(dataContext))}
                     />
                  }
               />
            </>
         )}
      </div>
   )
}
